/**
 * eBay account connection — client side of the OAuth flow (eBay step 4).
 *
 * The heavy lifting lives in two Edge Functions:
 *   ebay-oauth-start    (authenticated) — start / status / disconnect
 *   ebay-oauth-callback (public)        — code exchange + token storage
 * Tokens NEVER reach the browser; this module only sees connection status.
 * The callback bounces the browser back to the app with #ebay-connected or
 * #ebay-error=<why>, which the module-load check below turns into a toast.
 */
import { sb } from "./supabase";
import { escapeHtml, toast } from "./dom";

export interface EbayStatus {
  connected: boolean;
  environment?: string;
  ebay_username?: string | null;
  access_expires_at?: string | null;
  refresh_expires_at?: string | null;
  connected_at?: string | null;
  error?: string;
}

export async function ebayStatus(): Promise<EbayStatus> {
  try {
    const { data, error } = await sb.functions.invoke("ebay-oauth-start", { body: { action: "status" } });
    if (error) throw error;
    return data as EbayStatus;
  } catch (e: any) {
    return { connected: false, error: e?.message || String(e) };
  }
}

async function ebayConnectStart(): Promise<void> {
  try {
    const returnUrl = window.location.origin + window.location.pathname;
    const { data, error } = await sb.functions.invoke("ebay-oauth-start", { body: { action: "start", returnUrl } });
    if (error) throw error;
    const url = (data as any)?.url;
    if (!url) throw new Error((data as any)?.error || "No auth URL returned");
    window.location.href = url; // eBay sign-in + consent, then back via the callback
  } catch (e: any) {
    toast("Could not start eBay connect: " + (e?.message || e), "err");
  }
}

async function ebayDisconnect(): Promise<void> {
  if (!confirm("Disconnect your eBay account? The stored access tokens are deleted. (You can also revoke access anytime from your eBay account settings.)")) return;
  try {
    const { error } = await sb.functions.invoke("ebay-oauth-start", { body: { action: "disconnect" } });
    if (error) throw error;
    toast("eBay disconnected");
    void refreshEbayCard();
  } catch (e: any) {
    toast("Disconnect failed: " + (e?.message || e), "err");
  }
}

/** Fill the "eBay connection" card in Configs \u00b7 Data Management (if mounted). */
export async function refreshEbayCard(): Promise<void> {
  const el = document.getElementById("ebayConnStatus");
  if (!el) return;
  el.textContent = "Checking\u2026";
  const s = await ebayStatus();
  const btnC = document.getElementById("btnEbayConnect");
  const btnD = document.getElementById("btnEbayDisconnect");
  if (s.connected) {
    const until = s.refresh_expires_at ? new Date(s.refresh_expires_at).toLocaleDateString() : "?";
    el.innerHTML = `<span class="text-emerald-300">\u2713 Connected</span> as <b>${escapeHtml(s.ebay_username || "(unknown user)")}</b>` +
      ` \u00b7 ${escapeHtml(s.environment || "")} \u00b7 connection valid until ${escapeHtml(until)}`;
    btnC?.classList.add("hidden");
    btnD?.classList.remove("hidden");
  } else {
    el.innerHTML = s.error
      ? `<span class="text-amber-300">Status unavailable:</span> ${escapeHtml(s.error)}`
      : `Not connected.${(s.environment || "") === "sandbox" ? ' <span class="text-amber-300">(sandbox mode)</span>' : ""}`;
    btnC?.classList.remove("hidden");
    btnD?.classList.add("hidden");
  }
}

// Post-OAuth landing notice (the callback redirects here with a hash).
(() => {
  const h = window.location.hash || "";
  if (h.startsWith("#ebay-connected")) {
    const user = decodeURIComponent(h.split("=")[1] || "");
    setTimeout(() => toast(`eBay connected${user ? ": " + user : ""} \u2713`), 400);
    history.replaceState(null, "", window.location.pathname + window.location.search);
  } else if (h.startsWith("#ebay-error")) {
    const why = decodeURIComponent(h.split("=")[1] || "unknown");
    setTimeout(() => toast("eBay connection failed: " + why, "err"), 400);
    history.replaceState(null, "", window.location.pathname + window.location.search);
  }
})();

// Inline-handler bridges
(window as any)._ebayConnect    = ebayConnectStart;
(window as any)._ebayDisconnect = ebayDisconnect;
